import { createElement, useCallback, useContext } from 'react'
import ModalContext from 'contexts/ModalContext'
import { useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import getIsLoyaltyEsimEnabled from 'loyalty/selectors/getIsLoyaltyEsimEnabled'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import LoyaltyEsimDetailsModal from 'loyalty/components/LoyaltyEsimDetailsModal'
import LoyaltyEsimBenefitsInfoModal from 'loyalty/components/LoyaltyEsimBenefitsInfoModal'
import {
  LOYALTY_ESIM_DETAILS_HASH,
  LOYALTY_ESIM_INFO_HASH,
} from 'loyalty/constants/loyaltyRoutes'
import useOpenModalOnHash from './useOpenModalOnHash'

interface UseEsimHashModalOptions {
  orderId?: string
}

function useEsimHashModal(options?: UseEsimHashModalOptions) {
  const showModal = useContext(ModalContext)
  const loggedIn = useAppSelector(selectLoggedIn)
  const isEsimEnabled = useAppSelector(getIsLoyaltyEsimEnabled)
  const orderId = options?.orderId

  const openDetailsModal = useCallback(async() => {
    if (!isEsimEnabled) return
    const modalElement = createElement(LoyaltyEsimDetailsModal, orderId ? { orderId } : undefined)
    if (loggedIn) {
      await showModal(modalElement)
      return
    }

    const result = await showModal<AccountAccessModalResult>(
      createElement(AccountAccessModal, { initialMode: 'login' }),
    )
    if (result?.loggedIn) {
      await showModal(modalElement)
    }
  }, [isEsimEnabled, loggedIn, showModal, orderId])

  const openInfoModal = useCallback(async() => {
    if (!isEsimEnabled) return
    if (loggedIn) {
      await showModal(createElement(LoyaltyEsimBenefitsInfoModal))
      return
    }

    const result = await showModal<AccountAccessModalResult>(
      createElement(AccountAccessModal, { initialMode: 'login' }),
    )
    if (result?.loggedIn) {
      await showModal(createElement(LoyaltyEsimBenefitsInfoModal))
    }
  }, [isEsimEnabled, loggedIn, showModal])

  useOpenModalOnHash(LOYALTY_ESIM_DETAILS_HASH, openDetailsModal)
  useOpenModalOnHash(LOYALTY_ESIM_INFO_HASH, openInfoModal)
}

export default useEsimHashModal
